import {
    getSupabaseDocumentTypes,
    toggleDocumentSupabase,
    getDocumentTemplates,
    getSharedDocIDs,
    addSelectedDocumentForDoctor,
    removeSelectedDocumentForDoctor,
    addSelectedHistoryDocumentForDoctor,
    removeSelectedHistoryDocumentForDoctor,
    fetchHiddenDocumentsFromAccount,
} from "@common/lib/supabaseClient";
import { DocumentType } from "@common/utils/types";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";

/**
 * getLocalDocumentTypes is a helper function to get the selected documents from local storage.
 * It checks if the data is in the legacy format and converts it to the new format if needed.
 * @returns {string[]} - The array of selected document IDs.
 */
const getLocalDocumentTypes = () => {
    const docs = localStorage.getItem("selectedDocuments");
    try {
        if (docs) {
            const parsedDocs = JSON.parse(docs);

            // Check if the parsedDocs is an array of objects (legacy format)
            if (Array.isArray(parsedDocs) && parsedDocs.length > 0 && typeof parsedDocs[0] === "object") {
                // Convert legacy format to new format (array of IDs)
                const docIds = parsedDocs.map((doc: { id: string }) => doc.id);
                // Store the converted data back to local storage
                localStorage.setItem("selectedDocuments", JSON.stringify(docIds));
                return docIds;
            }

            // If already in the new format, return as is
            return parsedDocs;
        }
        return [];
    } catch (e) {
        Sentry.captureException(e);
        return [];
    }
};

/**
 * getLocalHistoryDocumentTypes is a helper function to get the selected history documents from local storage.
 * @returns {string[]} - The array of selected history document IDs.
 */
const getLocalHistoryDocumentTypes = () => {
    const docs = localStorage.getItem("selectedHistoryDocuments");
    try {
        if (docs) {
            const parsedDocs = JSON.parse(docs);
            return Array.isArray(parsedDocs) ? parsedDocs : [];
        }
        return [];
    } catch (e) {
        Sentry.captureException(e);
        return [];
    }
};

// HAPPYDOC-5MK: an aborted request (e.g. PatientView.tsx's useEffect cleanup cancelling an
// in-flight fetch on navigate-away) isn't shaped like an HTTP error -- it has no `.response`.
// Thunks below must recognize it before touching `.response.data`, and let it propagate as-is
// so callers/reducers can tell a benign cancellation apart from a real failure.
const isAbortError = (error: unknown): boolean => (error as any)?.name === "AbortError";

const rejectValuePayload = (error: any) => error?.response?.data ?? error?.message;

const initialState: {
    documentTypes: DocumentType[];
    documentTemplates: DocumentType[];
    ownedSharedDocumentIds: string[];
    selectedDocuments: string[];
    selectedHistoryDocuments: string[];
    hiddenDocuments: string[];
    status: "idle" | "loading" | "succeeded" | "failed";
    error: string | null;
    lastDocumentFetchTime: number;
} = {
    documentTypes: [],
    documentTemplates: [],
    ownedSharedDocumentIds: [],
    hiddenDocuments: [],
    selectedDocuments: getLocalDocumentTypes() || [],
    selectedHistoryDocuments: getLocalHistoryDocumentTypes() || [],
    status: "idle",
    error: null,
    lastDocumentFetchTime: 0,
};

export const fetchDocumentTypes = createAsyncThunk(
    "document/fetchDocumentTypes",
    async (
        { accountID, hiddenDocIDs, signal }: { accountID: string; hiddenDocIDs: string[]; signal: AbortSignal },
        { rejectWithValue },
    ) => {
        try {
            const documentTypes = await getSupabaseDocumentTypes(signal, hiddenDocIDs, accountID);
            return documentTypes;
        } catch (error: any) {
            if (isAbortError(error)) throw error;
            return rejectWithValue(rejectValuePayload(error));
        }
    },
);

export const fetchDocumentTemplates = createAsyncThunk(
    "document/fetchDocumentTemplates",
    async ({ accountID, signal }: { accountID: string; signal: AbortSignal }, { rejectWithValue }) => {
        try {
            const documentTemplates = await getDocumentTemplates(signal, accountID);
            return documentTemplates;
        } catch (error: any) {
            if (isAbortError(error)) throw error;
            return rejectWithValue(rejectValuePayload(error));
        }
    },
);

export const fetchHiddenDocuments = createAsyncThunk(
    "document/fetchHiddenDocuments",
    async ({ signal, userId }: { signal: AbortSignal; userId?: string }, { rejectWithValue }) => {
        try {
            const hiddenDocuments = await fetchHiddenDocumentsFromAccount(signal, userId);
            return hiddenDocuments;
        } catch (error: any) {
            if (isAbortError(error)) throw error;
            return rejectWithValue(rejectValuePayload(error));
        }
    },
);

/**
 * fetchAllDocuments is an async thunk that fetches all documents for an account.
 * It fetches hidden documents, document templates, document types, and owned shared document IDs.
 * @param {string} accountID - The ID of the account to fetch documents for.
 * @param {AbortSignal} signal - The signal to cancel the fetch request.
 * @returns {Promise} - The promise containing the fetched data.
 */
export const fetchAllDocuments = createAsyncThunk(
    "document/fetchAllDocuments",
    async ({ accountID, signal }: { accountID: string; signal: AbortSignal }, { rejectWithValue, dispatch }) => {
        try {
            // Only fetchDocumentTypes depends on another call's result (hiddenDocuments, for
            // filtering). fetchDocumentTemplates and fetchOwnedSharedDocumentIds only need
            // accountID/signal, so run them alongside fetchHiddenDocuments instead of behind it —
            // chaining all four serially turned 2 necessary round trips into 4 for no reason.
            const [hiddenDocuments, documentTemplates, ownedSharedDocumentIds] = await Promise.all([
                dispatch(fetchHiddenDocuments({ userId: accountID, signal }) as any).unwrap(),
                dispatch(fetchDocumentTemplates({ accountID, signal }) as any).unwrap(),
                dispatch(fetchOwnedSharedDocumentIds({ accountID, signal }) as any).unwrap(),
            ]);
            const documentTypes = await dispatch(
                fetchDocumentTypes({ accountID, hiddenDocIDs: hiddenDocuments, signal }) as any,
            ).unwrap();

            return { hiddenDocuments, documentTemplates, documentTypes, ownedSharedDocumentIds };
        } catch (error: any) {
            if (isAbortError(error)) throw error;
            return rejectWithValue(rejectValuePayload(error));
        }
    },
);

/**
 * toggleDocumentInclude is an async thunk that toggles the include status of a document type.
 * It updates the document type in the database and the document types state.
 * @param {string} docTypeId - The ID of the document type to toggle.
 * @param {boolean} isActive - The new include status of the document type.
 * @returns {Promise} - The promise containing the fetched data.
 */
export const toggleDocumentInclude = createAsyncThunk(
    "document/toggleDocumentInclude",
    async ({ docTypeId, isActive }: { docTypeId: string; isActive: boolean }, { dispatch, rejectWithValue }) => {
        try {
            const error = await toggleDocumentSupabase(docTypeId, isActive);
            if (error) throw error;

            dispatch(documentIncludeToggled({ docTypeId, isActive }));

            // remove from selected documents
            if (!isActive) {
                await dispatch(removeDocumentForDoctor({ documentTypeId: docTypeId }));
            }
        } catch (error: any) {
            // No `signal` param on this thunk today -- if one is ever added, add the
            // `if (isAbortError(error)) throw error;` guard used by the thunks above too.
            return rejectWithValue(rejectValuePayload(error));
        }
    },
);

export const fetchOwnedSharedDocumentIds = createAsyncThunk(
    "document/fetchOwnedSharedDocumentIds",
    async ({ accountID, signal }: { accountID: string; signal: AbortSignal }, { rejectWithValue }) => {
        try {
            const ownedSharedDocumentIds = await getSharedDocIDs(signal, accountID);
            return ownedSharedDocumentIds;
        } catch (error: any) {
            if (isAbortError(error)) throw error;
            return rejectWithValue(rejectValuePayload(error));
        }
    },
);

/**
 * addDocumentForDoctor is an async thunk that adds a document type for a doctor.
 * It adds the document to the selected documents state and local storage.
 * Also adds the document to the doctor's selected documents in the database.
 * @param {string} documentTypeId - The ID of the document to add.
 * @param {string} doctorId - The ID of the doctor to add the document to.
 */
export const addDocumentForDoctor = createAsyncThunk(
    "doctors/addDocumentForDoctor",
    async (
        { documentTypeId, doctorId }: { documentTypeId: string; doctorId?: string },
        { dispatch, rejectWithValue, getState },
    ) => {
        try {
            const state = getState() as any;
            const selectedDoctor = state.doctors.selectedDoctor;

            // add to selected documents state
            dispatch(addSelectedDocument(documentTypeId));

            if (doctorId || selectedDoctor) {
                // add to selected documents in the database
                const error = await addSelectedDocumentForDoctor(doctorId || selectedDoctor.id, documentTypeId);

                if (error) throw error;

                return;
            }
        } catch (error: any) {
            // No `signal` param on this thunk today -- if one is ever added, add the
            // `if (isAbortError(error)) throw error;` guard used by the fetch* thunks above too.
            return rejectWithValue(rejectValuePayload(error));
        }
    },
);

/**
 * addHistoryDocumentForDoctor is an async thunk that adds a document type for a doctor in history context.
 * It adds the document to the selected history documents state and local storage.
 * Also adds the document to the doctor's selected history documents in the database.
 * @param {string} documentTypeId - The ID of the document to add.
 * @param {string} doctorId - The ID of the doctor to add the document to.
 */
export const addHistoryDocumentForDoctor = createAsyncThunk(
    "doctors/addHistoryDocumentForDoctor",
    async (
        { documentTypeId, doctorId }: { documentTypeId: string; doctorId?: string },
        { dispatch, rejectWithValue, getState },
    ) => {
        try {
            const state = getState() as any;
            const selectedDoctor = state.doctors.selectedDoctor;

            // add to selected history documents state
            dispatch(addSelectedHistoryDocument(documentTypeId));

            if (doctorId || selectedDoctor) {
                // add to selected history documents in the database
                const error = await addSelectedHistoryDocumentForDoctor(doctorId || selectedDoctor.id, documentTypeId);

                if (error) throw error;

                return;
            }
        } catch (error: any) {
            // No `signal` param on this thunk today -- if one is ever added, add the
            // `if (isAbortError(error)) throw error;` guard used by the fetch* thunks above too.
            return rejectWithValue(rejectValuePayload(error));
        }
    },
);

/**
 * removeDocumentForDoctor is a thunk that removes a document from doctor's selected documents, selected document state and local storage.
 * @param {string} documentTypeId - The ID of the document to remove.
 * @param {string} doctorId - The ID of the doctor to remove the document from.
 */
export const removeDocumentForDoctor = createAsyncThunk(
    "doctors/removeDocumentForDoctor",
    async (
        { documentTypeId, doctorId }: { documentTypeId: string; doctorId?: string },
        { dispatch, rejectWithValue, getState },
    ) => {
        try {
            // get the selected doctor from the state
            const state = getState() as any;
            const selectedDoctor = state.doctors.selectedDoctor;

            // remove from selected documents state
            dispatch(removeSelectedDocument(documentTypeId));

            // remove from the doctor's selected documents from the database
            if (doctorId || selectedDoctor) {
                // remove from selected documents in the database
                const error = await removeSelectedDocumentForDoctor(doctorId || selectedDoctor.id, documentTypeId);
                if (error) throw error;

                return;
            }
        } catch (error: any) {
            // No `signal` param on this thunk today -- if one is ever added, add the
            // `if (isAbortError(error)) throw error;` guard used by the fetch* thunks above too.
            return rejectWithValue(rejectValuePayload(error));
        }
    },
);

/**
 * removeHistoryDocumentForDoctor is a thunk that removes a document from doctor's selected history documents, selected history document state and local storage.
 * @param {string} documentTypeId - The ID of the document to remove.
 * @param {string} doctorId - The ID of the doctor to remove the document from.
 */
export const removeHistoryDocumentForDoctor = createAsyncThunk(
    "doctors/removeHistoryDocumentForDoctor",
    async (
        { documentTypeId, doctorId }: { documentTypeId: string; doctorId?: string },
        { dispatch, rejectWithValue, getState },
    ) => {
        try {
            // get the selected doctor from the state
            const state = getState() as any;
            const selectedDoctor = state.doctors.selectedDoctor;

            // remove from selected history documents state
            dispatch(removeSelectedHistoryDocument(documentTypeId));

            // remove from the doctor's selected history documents from the database
            if (doctorId || selectedDoctor) {
                // remove from selected history documents in the database
                const error = await removeSelectedHistoryDocumentForDoctor(
                    doctorId || selectedDoctor.id,
                    documentTypeId,
                );
                if (error) throw error;

                return;
            }
        } catch (error: any) {
            // No `signal` param on this thunk today -- if one is ever added, add the
            // `if (isAbortError(error)) throw error;` guard used by the fetch* thunks above too.
            return rejectWithValue(rejectValuePayload(error));
        }
    },
);

const documentSlice = createSlice({
    name: "document",
    initialState,
    reducers: {
        documentTypeAdded(state, action) {
            const doc = state.documentTypes.find((doc) => doc.id === action.payload.id);
            if (!doc) state.documentTypes.push(action.payload);
        },
        documentTypeRemoved(state, action) {
            state.documentTypes = state.documentTypes.filter((doc) => doc.id !== action.payload);
        },
        documentIncludeToggled(state, action) {
            const { docTypeId, isActive } = action.payload;

            // update document types
            state.documentTypes = state.documentTypes.map((doc) =>
                doc.id === docTypeId ? { ...doc, is_active: isActive } : doc,
            );
        },
        sharedDocumentHiddenToggled(state, action) {
            const { docTypeId } = action.payload;

            // check if the document type ID is already in hiddenDocuments
            const isHidden = state.hiddenDocuments.includes(docTypeId);

            if (isHidden) {
                // if it's hidden, remove it from hiddenDocuments
                state.hiddenDocuments = state.hiddenDocuments.filter((doc) => doc !== docTypeId);
                state.documentTypes = state.documentTypes.map((doc) =>
                    doc.id === docTypeId ? { ...doc, is_active: true } : doc,
                );
            } else {
                // otherwise, add it to hiddenDocuments
                state.hiddenDocuments.push(docTypeId);
            }
        },
        addSelectedDocument(state, action: { payload: string }) {
            // get document by id
            const doc = state.documentTypes.find((doc) => doc.id === action.payload);

            // check if the document is already in selectedDocuments
            const isAlreadySelected = state.selectedDocuments.includes(action.payload);

            if (doc && doc.id && !isAlreadySelected) {
                state.selectedDocuments.push(doc.id);
                localStorage.setItem("selectedDocuments", JSON.stringify(state.selectedDocuments));
            }
        },
        documentSelected(state, action: { payload: string[] }) {
            // get ids of selected documents and ensure they're unique and not null and are in documentTypes

            const selectedDocs = action.payload.filter((id) => {
                const doc = state.documentTypes.find((doc) => doc.id === id);
                return doc && doc.id;
            });

            state.selectedDocuments = selectedDocs;
            localStorage.setItem("selectedDocuments", JSON.stringify(selectedDocs));
        },
        removeSelectedDocument(state, action: { payload: string }) {
            state.selectedDocuments = state.selectedDocuments.filter((selectedId) => selectedId !== action.payload);
            localStorage.setItem("selectedDocuments", JSON.stringify(state.selectedDocuments));
        },
        addSelectedHistoryDocument(state, action: { payload: string }) {
            // get document by id
            const doc = state.documentTypes.find((doc) => doc.id === action.payload);

            // check if the document is already in selectedHistoryDocuments
            const isAlreadySelected = state.selectedHistoryDocuments.includes(action.payload);

            if (doc && doc.id && !isAlreadySelected) {
                state.selectedHistoryDocuments.push(doc.id);
                localStorage.setItem("selectedHistoryDocuments", JSON.stringify(state.selectedHistoryDocuments));
            }
        },
        historyDocumentSelected(state, action: { payload: string[] }) {
            // get ids of selected history documents and ensure they're unique and not null and are in documentTypes

            const selectedDocs = action.payload.filter((id) => {
                const doc = state.documentTypes.find((doc) => doc.id === id);
                return doc && doc.id;
            });

            state.selectedHistoryDocuments = selectedDocs;
            localStorage.setItem("selectedHistoryDocuments", JSON.stringify(selectedDocs));
        },
        removeSelectedHistoryDocument(state, action: { payload: string }) {
            state.selectedHistoryDocuments = state.selectedHistoryDocuments.filter(
                (selectedId) => selectedId !== action.payload,
            );
            localStorage.setItem("selectedHistoryDocuments", JSON.stringify(state.selectedHistoryDocuments));
        },
        clearDocumentState(state) {
            state.documentTypes = [];
            state.selectedDocuments = [];
            state.selectedHistoryDocuments = [];
            state.documentTemplates = [];
            state.hiddenDocuments = [];
            localStorage.removeItem("selectedDocuments");
            localStorage.removeItem("selectedHistoryDocuments");
        },
    },
    extraReducers: (builder) => {
        builder
            // fetchDocumentTypes/fetchDocumentTemplates/fetchHiddenDocuments/fetchOwnedSharedDocumentIds
            // are only ever dispatched together, from fetchAllDocuments below, and (since it
            // parallelized them) can now settle in any order relative to each other. `status`/`error`
            // used to be safe as a shared field only because these four ran strictly one at a time;
            // under concurrency, whichever settles last would win and could flip a real failure to
            // "succeeded" or vice versa. Each keeps its own data-field write (those don't overlap),
            // but status/error now come solely from fetchAllDocuments's own lifecycle below.
            .addCase(fetchDocumentTypes.fulfilled, (state, action) => {
                state.documentTypes = action.payload;
            })
            // Code-review finding (2026-09-20): unlike the other three sub-thunks below,
            // fetchDocumentTypes IS dispatched standalone outside fetchAllDocuments (see
            // IndividualDocType.tsx's onSave/onDuplicate, fire-and-forget after saving a document
            // type). Without this, a failure there vanished with zero observability. Reports to
            // Sentry only -- state.status/state.error stay exclusively owned by fetchAllDocuments's
            // own lifecycle (see comment above) so this can't reintroduce the concurrency race.
            .addCase(fetchDocumentTypes.rejected, (_state, action) => {
                // action.error is always a plain serialized object (miniSerializeError), never an
                // Error/DOMException instance, so Sentry's beforeSend AbortError instanceof-filter
                // can't recognize it here -- filter it explicitly instead (HAPPYDOC-5MK).
                if (action.error?.name === "AbortError") return;
                Sentry.captureException(action.error, {
                    extra: { thunk: "fetchDocumentTypes", payload: action.payload },
                });
            })
            .addCase(fetchDocumentTemplates.fulfilled, (state, action) => {
                state.documentTemplates = action.payload; // Update the documentTemplates state with the fetched data
            })
            .addCase(fetchHiddenDocuments.fulfilled, (state, action) => {
                state.hiddenDocuments = action.payload || []; // Update the documentTemplates state with the fetched data
            })
            .addCase(fetchAllDocuments.pending, (state) => {
                state.status = "loading";
            })
            .addCase(fetchAllDocuments.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.hiddenDocuments = action.payload.hiddenDocuments;
                state.documentTemplates = action.payload.documentTemplates;
                state.documentTypes = action.payload.documentTypes;
                state.ownedSharedDocumentIds = action.payload.ownedSharedDocumentIds;

                // Filter out archived and inactive documents
                if (state.documentTypes.length > 0) {
                    state.selectedDocuments = state.selectedDocuments.filter((selectedId) =>
                        state.documentTypes.some((type) => type.id === selectedId && type.is_active),
                    );
                    state.selectedHistoryDocuments = state.selectedHistoryDocuments.filter((selectedId) =>
                        state.documentTypes.some((type) => type.id === selectedId && type.is_active),
                    );
                }

                state.lastDocumentFetchTime = Date.now();

                // Update local storage with new selectedDocuments
                localStorage.setItem("selectedDocuments", JSON.stringify(state.selectedDocuments));
                localStorage.setItem("selectedHistoryDocuments", JSON.stringify(state.selectedHistoryDocuments));
            })
            .addCase(fetchAllDocuments.rejected, (state, action) => {
                // A cancelled navigation isn't a failed fetch -- leave status/error untouched
                // (HAPPYDOC-5MK); see isAbortError's comment above for why action.error can't be
                // checked with instanceof here.
                if (action.error?.name === "AbortError") return;
                state.status = "failed";
                state.error = action.error?.message || null;
            })
            .addCase(fetchOwnedSharedDocumentIds.fulfilled, (state, action) => {
                state.ownedSharedDocumentIds = action.payload;
            });
    },
});

export const {
    documentTypeAdded,
    documentIncludeToggled,
    sharedDocumentHiddenToggled,
    addSelectedDocument,
    documentSelected,
    documentTypeRemoved,
    removeSelectedDocument,
    addSelectedHistoryDocument,
    historyDocumentSelected,
    removeSelectedHistoryDocument,
    clearDocumentState,
} = documentSlice.actions;

export default documentSlice.reducer;
